import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { API_BASE_URL } from '@/config/api-config';

export const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      console.log('[EMAIL VERIFY] Verifying email with token...');
      
      const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        credentials: 'include'
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || result.message || 'Verification failed');
      }

      if (result.success) {
        setStatus('success');
        setMessage(result.message || 'Email verified successfully!');
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/auth/login?verified=true');
        }, 3000);
      } else {
        throw new Error(result.message || 'Verification failed');
      }
    } catch (error: any) {
      console.error('[EMAIL VERIFY] Failed:', error);
      setStatus('error');
      setMessage(error.message || 'Email verification failed. The link may be expired.');
    }
  };

  return (
    <AuthLayout title="Email Verification">
      <div className="flex flex-col items-center justify-center py-12">
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-gray-400 animate-spin" />
            <h2 className="text-2xl font-medium mb-2">Verifying your email...</h2>
            <p className="text-gray-600">Please wait a moment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-2xl font-medium mb-2">Email Verified!</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">Redirecting you to login...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-2xl font-medium mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/auth/login')}
              className="px-6 py-3 bg-black text-white hover:bg-gray-800 transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};
